import React, {useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {Trans} from '@lingui/react';
import {t} from '@lingui/core/macro';

import Button from 'shared/ui/Button/Button/Button';
import Plus from 'shared/ui/icons/Plus';
import Clock from 'shared/ui/icons/General/Clock';
import Envelope from 'shared/ui/icons/Envelope';
import TelegramNotBackground from 'shared/ui/icons/TelegramNotBackground';
import MaxChat from 'shared/ui/icons/MaxChat';
import UserReportModal from 'features/userReports/ui/UserReportModal/UserReportModal';

import ScreenshotPreview1 from 'images/report-preview-1.png';
import ScreenshotPreview2 from 'images/report-preview-2.png';
import ScreenshotPreview3 from 'images/report-preview-3.png';
import ScreenshotPreview4 from 'images/report-preview-4.png';
import ScreenshotPreview5 from 'images/report-preview-5.png';

import {PALETTE} from 'shared/config/model/palette';

import ScreenshotCarousel from './ScreenshotCarousel/ScreenshotCarousel';

import styles from './UserReportsWelcome.module.scss';

const SCREENSHOTS = [
    ScreenshotPreview1,
    ScreenshotPreview2,
    ScreenshotPreview3,
    ScreenshotPreview4,
    ScreenshotPreview5,
];

const UserReportsWelcome: React.FC = () => {
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    const schedules = [
        {
            label: t`Раз в неделю`,
            timing: t`Формируется после 00:00 в понедельник`,
        },
        {
            label: t`Раз в месяц`,
            timing: t`В 00:00 1-го числа следующего месяца`,
        },
        {
            label: t`Раз в год`,
            timing: t`В 00:00 1 января следующего года`,
        },
    ];

    const channels = [
        {icon: <Envelope color={PALETTE.accent}/>, label: t`Почта`},
        {icon: <TelegramNotBackground/>, label: t`Telegram`},
        {icon: <MaxChat color="colored"/>, label: t`MAX`},
    ];

    return (
        <div className={styles.page}>
            <Helmet>
                <title>{`${t`Отчёты`} | ${t`Overseer`}`}</title>
            </Helmet>

            <section className={styles.hero}>
                <div className={styles.heroText}>
                    <h1 className={styles.title}>
                        <Trans id="reports.welcome.title" message="Регулярные отчёты по вашим мониторам"/>
                    </h1>
                    <p className={styles.subtitle}>
                        <Trans
                            id="reports.welcome.subtitle"
                            message="Настройте автоматический отчёт о доступности и инцидентах — он сам
                                сформируется по расписанию и придёт туда, где вам удобно."
                        />
                    </p>
                    <Button variant="gradient" className={styles.cta} onClick={() => setIsCreateOpen(true)}>
                        <Plus width={45} height={45}/>
                        <Trans id="reports.welcome.cta" message="Создать отчёт"/>
                    </Button>
                </div>
                <div className={styles.heroPreview}>
                    <ScreenshotCarousel images={SCREENSHOTS}/>
                </div>
            </section>

            <div className={styles.blocksRow}>
                <section className={styles.block}>
                    <div className={styles.blockHead}>
                        <h2 className={styles.blockTitle}>
                            <Trans id="reports.welcome.schedule.title" message="Выберите периодичность отправки"/>
                        </h2>
                        <p className={styles.blockText}>
                            <Trans
                                id="reports.welcome.schedule.text"
                                message="Отчёт формируется автоматически в начале следующего периода."
                            />
                        </p>
                    </div>
                    <div className={styles.scheduleGrid}>
                        {schedules.map((item) => (
                            <div className={styles.scheduleCard} key={item.label}>
                                <span className={styles.scheduleIcon}><Clock/></span>
                                <span className={styles.scheduleLabel}>{item.label}</span>
                                <span className={styles.scheduleTiming}>{item.timing}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className={styles.block}>
                    <div className={styles.blockHead}>
                        <h2 className={styles.blockTitle}>
                            <Trans id="reports.welcome.channels.title" message="Выберите, куда отправлять"/>
                        </h2>
                        <p className={styles.blockText}>
                            <Trans
                                id="reports.welcome.channels.text"
                                message="Готовый отчёт придёт на выбранные каналы."
                            />
                        </p>
                    </div>
                    <div className={styles.channels}>
                        {channels.map((channel) => (
                            <div className={styles.channel} key={channel.label}>
                                <span className={styles.channelIcon}>{channel.icon}</span>
                                <span>{channel.label}</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <UserReportModal
                open={isCreateOpen}
                handleClose={() => setIsCreateOpen(false)}
            />
        </div>
    );
};

export default UserReportsWelcome;
